// APExamPrepScreen.js — landing page scaffold for AP exam preparation resources.
//
// Ryan-feedback scaffold for "AP exam prep resources" (formula sheets, AP-style
// practice exams, final review modules). Structure only — actual content
// (formula PDFs, practice exam payloads, etc.) is content-team work.
import React from "react";
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { getMainContentColors } from "../components/MainContentArea";

const RESOURCES = [
  {
    id: "formula-sheets",
    icon: "function-variant",
    title: "Formula Sheets",
    subtitle: "Quick reference for AP Physics, Calculus, Chemistry, and more",
    accent: "#3b82f6",
    placeholder: "Content authoring in progress.",
  },
  {
    id: "ap-practice-exams",
    icon: "clipboard-text-outline",
    title: "AP-Style Practice Exams",
    subtitle: "Full-length, timed practice modeled on official College Board format",
    accent: "#8b5cf6",
    placeholder: "Content authoring in progress.",
  },
  {
    id: "unit-summaries",
    icon: "book-open-page-variant-outline",
    title: "Unit Summaries",
    subtitle: "Concise unit-by-unit review notes with key concepts and worked examples",
    accent: "#10b981",
    placeholder: "Content authoring in progress.",
  },
  {
    id: "cumulative-review",
    icon: "school-outline",
    title: "Final Review Modules",
    subtitle: "Cumulative practice sets and exam-day strategies",
    accent: "#f59e0b",
    placeholder: "Content authoring in progress.",
  },
  {
    id: "cheat-sheets",
    icon: "note-text-outline",
    title: "Cheat Sheets",
    subtitle: "One-page summaries you can print or save offline",
    accent: "#ec4899",
    placeholder: "Content authoring in progress.",
  },
];

export default function APExamPrepScreen({ isDarkMode = false, t = (s) => s }) {
  const colors = getMainContentColors(isDarkMode);

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: colors.background }]}
      contentContainerStyle={styles.content}
    >
      {/* Header */}
      <View style={styles.header}>
        <View style={[styles.iconBadge, { backgroundColor: isDarkMode ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.1)" }]}>
          <MaterialCommunityIcons
            name="medal-outline"
            size={36}
            color={isDarkMode ? "#818cf8" : "#6366f1"}
          />
        </View>
        <Text style={[styles.title, { color: colors.textPrimary }]}>
          {t("AP Exam Prep")}
        </Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
          {t("Resources to help you prepare for AP exam day")}
        </Text>
      </View>

      {/* Resource cards */}
      <View style={styles.grid}>
        {RESOURCES.map((r) => (
          <TouchableOpacity
            key={r.id}
            activeOpacity={0.7}
            disabled
            style={[
              styles.card,
              {
                backgroundColor: colors.cardBg,
                borderColor: `${r.accent}33`,
                ...(Platform.OS === "web"
                  ? {
                      background: isDarkMode
                        ? `linear-gradient(135deg, ${r.accent}15 0%, ${colors.cardBg} 100%)`
                        : `linear-gradient(135deg, ${r.accent}0a 0%, #ffffff 100%)`,
                      boxShadow: `0 2px 8px ${r.accent}15`,
                    }
                  : {}),
              },
            ]}
          >
            <View style={[styles.cardIcon, { backgroundColor: `${r.accent}1f` }]}>
              <MaterialCommunityIcons name={r.icon} size={28} color={r.accent} />
            </View>
            <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>
              {t(r.title)}
            </Text>
            <Text style={[styles.cardSubtitle, { color: colors.textSecondary }]} numberOfLines={2}>
              {t(r.subtitle)}
            </Text>
            <View style={[styles.placeholderRow, { borderTopColor: colors.border || (isDarkMode ? "#334155" : "#e7e5e4") }]}>
              <MaterialCommunityIcons
                name="clock-outline"
                size={12}
                color={colors.textMuted}
                style={{ marginRight: 6 }}
              />
              <Text style={[styles.placeholderText, { color: colors.textMuted }]}>
                {t(r.placeholder)}
              </Text>
            </View>
          </TouchableOpacity>
        ))}
      </View>

      {/* Footer note */}
      <Text style={[styles.footerNote, { color: colors.textMuted }]}>
        {t("Have a request? Share it with your teacher to help us prioritize what gets built first.")}
      </Text>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, width: "100%" },
  content: { paddingHorizontal: 20, paddingVertical: 24, alignItems: "center" },
  header: { alignItems: "center", marginBottom: 28, maxWidth: 720 },
  iconBadge: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 14,
  },
  title: { fontSize: 28, fontWeight: "700", marginBottom: 8, textAlign: "center" },
  subtitle: { fontSize: 15, textAlign: "center", lineHeight: 22 },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 16,
    maxWidth: 1100,
    width: "100%",
  },
  card: {
    width: 320,
    borderRadius: 14,
    borderWidth: 1,
    padding: 18,
  },
  cardIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 14,
  },
  cardTitle: { fontSize: 17, fontWeight: "600", marginBottom: 6 },
  cardSubtitle: { fontSize: 13, lineHeight: 19, marginBottom: 14 },
  placeholderRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingTop: 10,
    borderTopWidth: 1,
  },
  placeholderText: { fontSize: 11, fontStyle: "italic" },
  footerNote: {
    fontSize: 12,
    textAlign: "center",
    marginTop: 28,
    fontStyle: "italic",
    maxWidth: 600,
  },
});
